import React, { useState, useEffect, useRef } from 'react';
import { 
    Badge, 
    Button, 
    Dropdown, 
    List, 
    Typography, 
    Empty, 
    Divider, 
    Tag, 
    Avatar,
    Space,
    Tooltip,
    Modal,
    Switch,
    Select,
    notification as antNotification
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
    BellOutlined, 
    CheckOutlined, 
    DeleteOutlined,
    SettingOutlined,
    ShoppingCartOutlined,
    UserOutlined,
    CalendarOutlined,
    StarOutlined,
    GiftOutlined,
    WarningOutlined,
    FileTextOutlined,
    DollarOutlined,
    CreditCardOutlined,
    MoneyCollectOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import hybridChatService from '../../services/hybridChat';
import soundNotificationService from '../../services/soundNotification';
import pushNotificationService from '../../services/pushNotificationService';
import NotificationPreferences from './NotificationPreferences';
import PushNotificationPrompt from './PushNotificationPrompt';

const { Text, Title } = Typography;

const NotificationCenter = ({ className = "" }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState(() => {
        try { return JSON.parse(localStorage.getItem('notifications_feed') || '[]'); } catch (_) { return []; }
    });
    const [unreadCount, setUnreadCount] = useState(() => {
        const val = localStorage.getItem('notifications_unread');
        return val ? parseInt(val, 10) || 0 : 0;
    });
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(() => {
        try { return soundNotificationService.isSoundEnabled(); } catch(_) { return true; }
    });
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [notificationPrefsOpen, setNotificationPrefsOpen] = useState(false);
    const [pushPromptOpen, setPushPromptOpen] = useState(false);
    const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
    const [pushPromptDismissed, setPushPromptDismissed] = useState(() => {
        try { return localStorage.getItem('push_prompt_dismissed') === 'true'; } catch(_) { return false; }
    });
    const [filters, setFilters] = useState(() => {
        try { return JSON.parse(localStorage.getItem('notification_filters') || '{}'); } catch(_) { return {}; }
    });
    const wsRef = useRef(null);

    useEffect(() => {
        if (!user) return;
        fetchNotifications();

        // Initialize push notifications
        const initPushNotifications = async () => {
            try {
                const isEnabled = pushNotificationService.isEnabled();
                setPushNotificationsEnabled(isEnabled);
                
                if (!isEnabled && pushNotificationService.isSupported && !pushPromptDismissed) {
                    // Show push notification prompt after a delay
                    setTimeout(() => {
                        setPushPromptOpen(true);
                    }, 3000);
                }
            } catch (error) {
                console.error('Failed to initialize push notifications:', error);
            }
        };

        initPushNotifications();

        // Initialize notification polling instead of real-time subscriptions
        // This is more reliable for shared hosting
        const init = async () => {
            try {
                // Poll for new notifications every 30 seconds
                const pollInterval = setInterval(async () => {
                    try {
                        // Check if user is authenticated before making API calls
                        const authUser = localStorage.getItem('auth_user');
                        if (!authUser || !user) {
                            console.log('User not authenticated, skipping notification polling');
                            return;
                        }
                        
                        const response = await api.get('/notifications?unread_only=true');
                        if (response.data.success && response.data.data.length > 0) {
                            response.data.data.forEach(notification => {
                                addNotification({
                                    id: notification.id,
                                    type: notification.type,
                                    title: notification.title,
                                    description: notification.message,
                                    timestamp: notification.created_at,
                                    data: notification.data,
                                    icon: getNotificationIcon(notification.type),
                                    color: getNotificationColor(notification.type)
                                });
                                try { soundNotificationService.playNotificationSound(); } catch(e) {}
                                
                                // Show push notification if enabled
                                if (pushNotificationService.isEnabled()) {
                                    pushNotificationService.showInAppNotification({
                                        title: notification.title,
                                        body: notification.message,
                                        data: notification.data
                                    });
                                }
                            });
                        }
                    } catch (error) {
                        console.error('Failed to poll notifications:', error);
                    }
                }, 30000);

                // Store interval reference for cleanup
                wsRef.current = pollInterval;
            } catch(e) {
                console.error('Failed to init notification polling', e);
            }
        };
        init();

        return () => {
            // Clear polling interval
            if (wsRef.current) {
                clearInterval(wsRef.current);
                wsRef.current = null;
            }
        };
    }, [user]);


    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await api.getNotifications();
            console.log('Fetched notifications response:', response);
            
            const notifications = response.data.data.map(notification => ({
                id: notification.id,
                type: notification.type,
                title: notification.title,
                message: notification.message,
                timestamp: notification.created_at,
                read: !!notification.read_at, // Check if read_at exists
                data: notification.data, // Include the data field for navigation
                icon: getNotificationIcon(notification.type),
                color: getNotificationColor(notification.type),
                priority: notification.priority
            }));

            console.log('Processed notifications:', notifications);
            const unreadCount = notifications.filter(n => !n.read).length;
            console.log('Unread count:', unreadCount);

            setNotifications(notifications);
            setUnreadCount(unreadCount);
            try { localStorage.setItem('notifications_feed', JSON.stringify(notifications)); } catch(_) {}
            try { localStorage.setItem('notifications_unread', String(unreadCount)); } catch(_) {}
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            // Fallback to cached notifications
            try {
                const cached = JSON.parse(localStorage.getItem('notifications_feed') || '[]');
                setNotifications(cached);
                setUnreadCount(cached.filter(n => !n.read).length);
            } catch(_) {}
        } finally {
            setLoading(false);
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'quote': return 'FileTextOutlined';
            case 'booking': return 'CalendarOutlined';
            case 'message': return 'UserOutlined';
            case 'promotion': return 'GiftOutlined';
            case 'system': return 'WarningOutlined';
            case 'payment': return 'DollarOutlined';
            case 'milestone': return 'CreditCardOutlined';
            case 'advance': return 'MoneyCollectOutlined';
            case 'review': return 'StarOutlined';
            default: return 'BellOutlined';
        }
    };

    const renderNotificationIcon = (iconType) => {
        switch (iconType) {
            case 'FileTextOutlined': return <FileTextOutlined />;
            case 'CalendarOutlined': return <CalendarOutlined />;
            case 'UserOutlined': return <UserOutlined />;
            case 'GiftOutlined': return <GiftOutlined />;
            case 'WarningOutlined': return <WarningOutlined />;
            case 'DollarOutlined': return <DollarOutlined />;
            case 'CreditCardOutlined': return <CreditCardOutlined />;
            case 'MoneyCollectOutlined': return <MoneyCollectOutlined />;
            case 'StarOutlined': return <StarOutlined />;
            default: return <BellOutlined />;
        }
    };

    const getNotificationColor = (type) => {
        switch (type) {
            case 'quote': return 'blue';
            case 'booking': return 'green';
            case 'message': return 'purple';
            case 'promotion': return 'orange';
            case 'system': return 'red';
            case 'payment': return 'green';
            case 'milestone': return 'blue';
            case 'advance': return 'orange';
            case 'review': return 'purple';
            default: return 'default';
        }
    };

    const addNotification = (notification) => {
        setNotifications(prev => {
            const next = [notification, ...prev].slice(0, 50);
            try { localStorage.setItem('notifications_feed', JSON.stringify(next)); } catch(_) {}
            return next;
        });
        setUnreadCount(prev => {
            const v = prev + 1; try { localStorage.setItem('notifications_unread', String(v)); } catch(_) {}
            return v;
        });
        
        // Show system notification
        antNotification.info({
            message: notification.title,
            description: notification.message,
            placement: 'topRight',
            duration: 4
        });
    };

    const markAsRead = async (notificationId) => {
        console.log('Marking notification as read:', notificationId);
        try {
            const response = await api.markNotificationAsRead(notificationId);
            console.log('Mark as read response:', response);
            
            // Update local state immediately for better UX
            setNotifications(prev => {
                const updated = prev.map(n => 
                    n.id === notificationId ? { ...n, read: true } : n
                );
                console.log('Updated notifications:', updated);
                return updated;
            });
            
            setUnreadCount(prev => {
                const v = Math.max(0, prev - 1);
                console.log('Updated unread count:', v);
                try { localStorage.setItem('notifications_unread', String(v)); } catch(_) {}
                return v;
            });
            
            // Refresh notifications from server to ensure accuracy
            setTimeout(() => {
                fetchNotifications();
            }, 500);
            
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
            // Still update UI even if API fails
            setNotifications(prev => 
                prev.map(n => 
                    n.id === notificationId ? { ...n, read: true } : n
                )
            );
            setUnreadCount(prev => {
                const v = Math.max(0, prev - 1);
                try { localStorage.setItem('notifications_unread', String(v)); } catch(_) {}
                return v;
            });
        }
    };

    const handleNotificationClick = async (notification) => {
        console.log('Notification clicked:', notification);
        console.log('Notification data:', notification.data);
        console.log('User type:', user.user_type);
        
        // Mark as read first and wait for completion
        if (!notification.read) {
            await markAsRead(notification.id);
        }
        
        // Navigate based on notification type and data
        if (notification.data && notification.data.url) {
            console.log('Navigating to URL:', notification.data.url);
            navigate(notification.data.url);
        } else if (notification.data && notification.data.lead_id) {
            if (user.user_type === 'vendor') {
                console.log('Navigating to vendor lead:', notification.data.lead_id);
                navigate(`/vendor/leads/${notification.data.lead_id}`);
            } else {
                console.log('Navigating to customer lead:', notification.data.lead_id);
                navigate(`/leads/${notification.data.lead_id}`);
            }
        } else if (notification.data && notification.data.quote_id) {
            console.log('Navigating to quote:', notification.data.quote_id);
            navigate(`/quotes/${notification.data.quote_id}`);
        } else if (notification.data && notification.data.booking_id) {
            console.log('Navigating to booking:', notification.data.booking_id);
            navigate(`/bookings/${notification.data.booking_id}`);
        } else {
            console.log('No specific data found, using default navigation for type:', notification.type);
            // Default navigation based on notification type
            switch (notification.type) {
                case 'new_lead':
                    navigate('/vendor/leads');
                    break;
                case 'lead_status_update':
                    navigate('/leads');
                    break;
                case 'quote':
                    navigate('/quotes');
                    break;
                case 'booking':
                    navigate('/bookings');
                    break;
                default:
                    navigate('/notifications');
            }
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.markAllNotificationsAsRead();
            setNotifications(prev => {
                const next = prev.map(n => ({ ...n, read: true }));
                try { localStorage.setItem('notifications_feed', JSON.stringify(next)); } catch(_) {}
                return next;
            });
            setUnreadCount(0);
            try { localStorage.setItem('notifications_unread', '0'); } catch(_) {}
            
            // Refresh notifications from server to ensure accuracy
            setTimeout(() => {
                fetchNotifications();
            }, 500);
            
        } catch (error) {
            console.error('Failed to mark all notifications as read:', error);
            // Still update UI even if API fails
            setNotifications(prev => {
                const next = prev.map(n => ({ ...n, read: true }));
                try { localStorage.setItem('notifications_feed', JSON.stringify(next)); } catch(_) {}
                return next;
            });
            setUnreadCount(0);
        }
    };

    const deleteNotification = async (notificationId) => {
        try {
            // Mock API call
            const notification = notifications.find(n => n.id === notificationId);
            setNotifications(prev => {
                const next = prev.filter(n => n.id !== notificationId);
                try { localStorage.setItem('notifications_feed', JSON.stringify(next)); } catch(_) {}
                return next;
            });
            if (!notification?.read) {
                setUnreadCount(prev => {
                    const v = Math.max(0, prev - 1);
                    try { localStorage.setItem('notifications_unread', String(v)); } catch(_) {}
                    return v;
                });
            }
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    };

    const getTimeAgo = (timestamp) => {
        const now = new Date();
        const time = new Date(timestamp);
        const diffInMinutes = Math.floor((now - time) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
        return `${Math.floor(diffInMinutes / 1440)}d ago`;
    };

    const notificationMenu = (
        <div className="w-80 max-h-96 overflow-hidden bg-white rounded-lg shadow-lg">
            <div className="p-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                    <Title level={5} className="!mb-0">Notifications</Title>
                    <Space>
                        {unreadCount > 0 && (
                            <Button 
                                type="link" 
                                size="small" 
                                onClick={markAllAsRead}
                                className="p-0"
                            >
                                Mark all read
                            </Button>
                        )}
                        <Tooltip title="Settings">
                            <Button 
                                type="text" 
                                size="small" 
                                icon={<SettingOutlined />}
                                className="p-1"
                                onClick={() => setSettingsOpen(true)}
                            />
                            <Button 
                                type="text" 
                                size="small" 
                                icon={<BellOutlined />}
                                className="p-1"
                                onClick={() => setNotificationPrefsOpen(true)}
                                title="Notification Preferences"
                            />
                        </Tooltip>
                    </Space>
                </div>
            </div>
            
            <div className="max-h-80 overflow-y-auto">
                {notifications.length > 0 ? (
                    <List
                        itemLayout="horizontal"
                        dataSource={(filters.types && filters.types.length)
                            ? notifications.filter(n => filters.types.includes(n.type))
                            : notifications}
                        renderItem={(item) => (
                            <List.Item
                                className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-l-4 ${
                                    !item.read ? 'border-l-blue-500 bg-blue-50' : 'border-l-transparent'
                                }`}
                                onClick={() => handleNotificationClick(item)}
                                actions={[
                                    <Tooltip title="Delete" key="delete">
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteNotification(item.id);
                                            }}
                                            className="text-gray-400 hover:text-red-500"
                                        />
                                    </Tooltip>
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Avatar 
                                            icon={renderNotificationIcon(item.icon)} 
                                            style={{ backgroundColor: item.color }}
                                            size="small"
                                        />
                                    }
                                    title={
                                        <div className="flex items-center justify-between">
                                            <span className={`text-sm ${!item.read ? 'font-semibold' : ''}`}>
                                                {item.title}
                                            </span>
                                            {!item.read && (
                                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                            )}
                                        </div>
                                    }
                                    description={
                                        <div>
                                            <div className="text-xs text-gray-600 mb-1">
                                                {item.message}
                                            </div>
                                            <div className="text-xs text-gray-400">
                                                {getTimeAgo(item.timestamp)}
                                            </div>
                                        </div>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                ) : (
                    <div className="p-8">
                        <Empty 
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No notifications"
                        />
                    </div>
                )}
            </div>
            
            {notifications.length > 0 && (
                <div className="p-3 border-t bg-gray-50 text-center">
                    <Button type="link" size="small" onClick={() => { window.location.href = '/notifications'; }}>
                        View All Notifications
                    </Button>
                </div>
            )}
        </div>
    );

    return (
        <>
            <Dropdown
                overlay={notificationMenu}
                trigger={['click']}
                placement="bottomRight"
                visible={visible}
                onVisibleChange={setVisible}
            >
                <Badge count={unreadCount} size="small" className={className}>
                    <Button 
                        type="text" 
                        icon={<BellOutlined />} 
                        shape="circle"
                        className="flex items-center justify-center"
                    />
                </Badge>
            </Dropdown>
            
            <PushNotificationPrompt
                visible={pushPromptOpen}
                onClose={() => {
                    setPushPromptOpen(false);
                    setPushPromptDismissed(true);
                    try { localStorage.setItem('push_prompt_dismissed', 'true'); } catch(_) {}
                }}
                onSuccess={() => {
                    setPushNotificationsEnabled(true);
                    setPushPromptOpen(false);
                }}
            />
            
            <Modal
                title="Notification Settings"
                open={settingsOpen}
                onCancel={() => setSettingsOpen(false)}
                onOk={() => setSettingsOpen(false)}
            >
                <Space direction="vertical" className="w-full">
                    <div className="flex items-center justify-between">
                        <span>Sound</span>
                        <Switch
                            checked={soundEnabled}
                            onChange={(checked) => {
                                setSoundEnabled(checked);
                                try { soundNotificationService.setEnabled(checked); } catch(_) {}
                            }}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div>
                            <div>Push Notifications</div>
                            <div className="text-xs text-gray-500">
                                {pushNotificationsEnabled ? 'Enabled' : 'Disabled'}
                            </div>
                        </div>
                        <Button
                            type={pushNotificationsEnabled ? 'default' : 'primary'}
                            size="small"
                            onClick={() => {
                                setPushPromptOpen(true);
                                // Reset dismissed state when user manually opens
                                setPushPromptDismissed(false);
                                try { localStorage.removeItem('push_prompt_dismissed'); } catch(_) {}
                            }}
                        >
                            {pushNotificationsEnabled ? 'Manage' : 'Enable'}
                        </Button>
                    </div>
                    <div>
                        <div className="mb-1">Show Types</div>
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Filter by type (leave empty to show all)"
                            className="w-full"
                            value={filters.types || []}
                            onChange={(vals) => {
                                const next = { ...(filters || {}), types: vals };
                                setFilters(next);
                                try { localStorage.setItem('notification_filters', JSON.stringify(next)); } catch(_) {}
                            }}
                            options={[
                                { label: 'Quotes', value: 'quote' },
                                { label: 'Bookings', value: 'booking' },
                                { label: 'Messages', value: 'message' },
                                { label: 'Promotions', value: 'promotion' },
                                { label: 'System', value: 'system' },
                                { label: 'Payments', value: 'payment' },
                                { label: 'Milestones', value: 'milestone' },
                                { label: 'Advance Requests', value: 'advance' },
                                { label: 'Reviews', value: 'review' },
                            ]}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <span>Auto-Mark Read on Open</span>
                        <Switch
                            checked={!!filters.autoMarkRead}
                            onChange={(checked) => {
                                const next = { ...(filters || {}), autoMarkRead: checked };
                                setFilters(next);
                                try { localStorage.setItem('notification_filters', JSON.stringify(next)); } catch(_) {}
                            }}
                        />
                    </div>
                </Space>
            </Modal>

            <NotificationPreferences
                visible={notificationPrefsOpen}
                onClose={() => setNotificationPrefsOpen(false)}
                onSave={(preferences) => {
                    console.log('Notification preferences saved:', preferences);
                    setNotificationPrefsOpen(false);
                }}
            />
        </>
    );
};

export default NotificationCenter;
